import { useState, useRef, useCallback, useEffect } from 'react';
import ReactCrop, { type Crop, type PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Loader2,
  RotateCcw,
  RotateCw,
  Crop as CropIcon,
  FlipHorizontal2,
  FlipVertical2,
  Undo2,
} from 'lucide-react';
import { SoftBackButton } from '@/components/SoftBackButton';
import { getCroppedImg } from '@/lib/cropUtils';
import { goldGradientActive } from '@/lib/gradientStyles';
import { cn } from '@/lib/utils';

interface ItemImageEditorDialogProps {
  open: boolean;
  imageSrc: string | null;
  onClose: () => void;
  onSave: (blob: Blob) => void;
}

type Tool = {
  id: string;
  label: string;
  Icon: typeof CropIcon;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
};

export function ItemImageEditorDialog({
  open,
  imageSrc,
  onClose,
  onSave,
}: ItemImageEditorDialogProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCropMode, setIsCropMode] = useState(false);

  // Undo history — snapshot stack of prior states
  type Snapshot = {
    rotation: number;
    flipH: boolean;
    flipV: boolean;
    crop: Crop | undefined;
    completedCrop: PixelCrop | undefined;
    isCropMode: boolean;
  };
  const [history, setHistory] = useState<Snapshot[]>([]);

  const snapshot = useCallback(
    (): Snapshot => ({ rotation, flipH, flipV, crop, completedCrop, isCropMode }),
    [rotation, flipH, flipV, crop, completedCrop, isCropMode]
  );

  const pushHistory = useCallback(() => {
    setHistory((h) => [...h, snapshot()].slice(-50));
  }, [snapshot]);

  const handleUndo = useCallback(() => {
    setHistory((h) => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1];
      setRotation(prev.rotation);
      setFlipH(prev.flipH);
      setFlipV(prev.flipV);
      setCrop(prev.crop);
      setCompletedCrop(prev.completedCrop);
      setIsCropMode(prev.isCropMode);
      return h.slice(0, -1);
    });
  }, []);

  const onImageLoad = useCallback((_e: React.SyntheticEvent<HTMLImageElement>) => {
    /* no-op */
  }, []);

  const handleActivateCrop = useCallback(() => {
    pushHistory();
    setIsCropMode(true);
    if (imgRef.current) {
      const { width, height } = imgRef.current;
      const size = Math.min(width, height);
      const initialCrop = centerCrop(
        makeAspectCrop({ unit: 'px', width: size }, 1, width, height),
        width,
        height
      );
      setCrop(initialCrop);
    }
  }, [pushHistory]);

  const handleClearCrop = useCallback(() => {
    pushHistory();
    setIsCropMode(false);
    setCrop(undefined);
    setCompletedCrop(undefined);
  }, [pushHistory]);

  const handleRotate = (delta: number) => {
    pushHistory();
    setRotation((r) => (r + delta) % 360);
  };

  const handleFlipH = () => {
    pushHistory();
    setFlipH((v) => !v);
  };

  const handleFlipV = () => {
    pushHistory();
    setFlipV((v) => !v);
  };

  const hasEdits = rotation !== 0 || flipH || flipV || !!completedCrop;

  const handleApply = async () => {
    if (!imageSrc || !imgRef.current) return;
    setIsProcessing(true);
    try {
      const img = imgRef.current;
      const scaleX = img.naturalWidth / img.width;
      const scaleY = img.naturalHeight / img.height;
      const cropArea = completedCrop
        ? { x: completedCrop.x, y: completedCrop.y, width: completedCrop.width, height: completedCrop.height }
        : { x: 0, y: 0, width: img.width, height: img.height };
      const croppedBlob = await getCroppedImg(imageSrc, cropArea, rotation, scaleX, scaleY);
      onSave(croppedBlob);
      handleClose();
    } catch (error) {
      console.error('Error processing image:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setCrop(undefined);
    setCompletedCrop(undefined);
    setRotation(0);
    setFlipH(false);
    setFlipV(false);
    setIsCropMode(false);
    setHistory([]);
    onClose();
  };

  useEffect(() => {
    if (open) {
      setRotation(0);
      setFlipH(false);
      setFlipV(false);
      setIsCropMode(false);
      setCrop(undefined);
      setCompletedCrop(undefined);
      setHistory([]);
    }
  }, [open, imageSrc]);

  if (!imageSrc) return null;

  const transform = `rotate(${rotation}deg) scaleX(${flipH ? -1 : 1}) scaleY(${flipV ? -1 : 1})`;
  const canUndo = history.length > 0;

  const tools: Tool[] = [
    {
      id: 'crop',
      label: isCropMode ? 'Cancel' : 'Crop',
      Icon: CropIcon,
      onClick: isCropMode ? handleClearCrop : handleActivateCrop,
      active: isCropMode,
    },
    { id: 'rl', label: 'Left', Icon: RotateCcw, onClick: () => handleRotate(-90) },
    { id: 'rr', label: 'Right', Icon: RotateCw, onClick: () => handleRotate(90) },
    { id: 'fh', label: 'Flip', Icon: FlipHorizontal2, onClick: handleFlipH, active: flipH },
    { id: 'fv', label: 'Mirror', Icon: FlipVertical2, onClick: handleFlipV, active: flipV },
    { id: 'undo', label: 'Undo', Icon: Undo2, onClick: handleUndo, disabled: !canUndo },
  ];

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent
        variant="fullscreen"
        className={cn(
          'p-0 overflow-hidden gap-0 border-white/[0.06] bg-chrome',
          'sm:max-w-[560px] sm:rounded-2xl'
        )}
      >
        <DialogTitle className="sr-only">Photo Editor</DialogTitle>

        {/* Top bar */}
        <div
          className="flex items-center justify-between px-3 sm:px-4 pt-3 sm:pt-4 pb-2"
          style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}
        >
          <SoftBackButton fallbackLabel="Back" onClick={handleClose} />
          {hasEdits && (
            <span className="text-[11px] uppercase tracking-[0.14em] text-white/70 font-medium">
              Edited
            </span>
          )}
        </div>

        {/* Canvas */}
        <div className="flex-1 px-3 sm:px-4 pb-3">
          <div
            className={cn(
              'relative w-full rounded-xl overflow-hidden flex items-center justify-center',
              'bg-[radial-gradient(ellipse_at_center,_rgba(255,255,255,0.04),_transparent_70%)]',
              'border border-white/[0.05]',
              'min-h-[55dvh] sm:min-h-[380px] sm:max-h-[460px]'
            )}
            style={{
              backgroundColor: '#0e1012',
            }}
          >
            {isCropMode ? (
              <>
                <style>{`
                  .ReactCrop__crop-selection {
                    border: 2px solid hsl(var(--active-gold)) !important;
                    box-shadow: 0 0 0 9999em rgba(0,0,0,0.55) !important;
                  }
                  .ReactCrop__drag-handle {
                    width: 14px !important;
                    height: 14px !important;
                    background-color: hsl(var(--active-gold)) !important;
                    border: 2px solid #0b0c0e !important;
                    border-radius: 9999px !important;
                  }
                  .ReactCrop__drag-handle::after { display: none !important; }
                `}</style>
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  onComplete={(c) => setCompletedCrop(c)}
                  className="max-h-[55dvh] sm:max-h-[440px]"
                >
                  <img
                    ref={imgRef}
                    src={imageSrc}
                    alt="Edit"
                    onLoad={onImageLoad}
                    style={{ transform, maxHeight: '55dvh', objectFit: 'contain' }}
                    crossOrigin="anonymous"
                  />
                </ReactCrop>
              </>
            ) : (
              <img
                ref={imgRef}
                src={imageSrc}
                alt="Edit"
                onLoad={onImageLoad}
                style={{ transform, maxHeight: '55dvh', objectFit: 'contain', transition: 'transform 200ms ease' }}
                crossOrigin="anonymous"
                className="sm:!max-h-[440px]"
              />
            )}
          </div>
          {isCropMode && (
            <div className="mt-2 flex justify-center">
              <span className="text-[11px] tracking-wide text-white/55">
                Drag corners to crop
              </span>
            </div>
          )}
        </div>

        {/* Tool rail */}
        <div className="px-2 sm:px-3 pb-2">
          <div className="flex items-stretch justify-between gap-1 overflow-x-auto no-scrollbar rounded-2xl bg-white/[0.03] border border-white/[0.05] p-1.5">
            {tools.map(({ id, label, Icon, onClick, active, disabled }) => (
              <button
                key={id}
                type="button"
                onClick={onClick}
                disabled={isProcessing || disabled}
                className={cn(
                  'group flex-1 min-w-[58px] flex flex-col items-center justify-center gap-1 px-2 py-2 rounded-xl',
                  'transition-all duration-150',
                  'disabled:opacity-30 disabled:cursor-not-allowed',
                  active
                    ? 'bg-active-gold/15 text-active-gold ring-1 ring-active-gold/40'
                    : 'text-white/70 hover:text-white hover:bg-white/[0.05] active:bg-white/[0.08]'
                )}
              >
                <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
                <span className="text-[10px] font-medium tracking-wide">{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Bottom action bar */}
        <div
          className="flex items-center justify-between gap-2 px-4 pt-2 pb-4"
          style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' }}
        >
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              className="text-white/60 hover:text-white hover:bg-white/[0.05]"
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setRotation(0);
                setFlipH(false);
                setFlipV(false);
                setIsCropMode(false);
                setCrop(undefined);
                setCompletedCrop(undefined);
                setHistory([]);
              }}
              disabled={isProcessing || !hasEdits}
              className="text-white/60 hover:text-white hover:bg-white/[0.05] disabled:opacity-30"
            >
              Reset
            </Button>
          </div>
          <Button
            type="button"
            onClick={handleApply}
            disabled={isProcessing}
            className={cn(goldGradientActive, 'h-10 px-6 rounded-full text-sm shadow-none')}
          >
            {isProcessing && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
            {hasEdits ? 'Apply' : 'Done'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
